<template>
  <div class="container mx-auto px-4 py-8">
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-100 mb-2">1:1 Personalisation</h1>
      <p class="text-gray-400">Create and manage 1:1 personalisation for your customers.</p>
    </div>

    <!-- Tabs -->
    <div class="mb-8">
      <div class="border-b border-gray-700">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="currentTab = tab.id"
            :class="[
              currentTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            ]"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Tab Content -->
    <div v-if="currentTab === 'schema'" class="mb-8">
      <SchemaBuilder 
        :modelValue="contextSchema"
        @save="saveSchema"
        :isSaving="isSavingSchema"
        title="Personalised Data Schema"
        subtitle="Define the structure of your personalised data for callbacks"
      />
    </div>

    <div v-else-if="currentTab === 'integration'" class="mb-8">
      <SchemaBuilder 
        :modelValue="integrationSchema"
        @save="saveIntegrationSchema"
        :isSaving="isSavingIntegrationSchema"
        title="Integration Data Schema"
        subtitle="Define the structure of your integration data for external systems"
      />
    </div>

    <div v-else-if="currentTab === 'callbacks'" class="space-y-8">
      <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
      <p class="text-red-400">{{ error }}</p>
      <button 
        @click="loadCallbacks"
        class="mt-2 text-sm text-red-400 hover:text-red-300"
      >
        Try Again
      </button>
    </div>

    <!-- Callbacks List -->
    <div v-else class="space-y-6">
      <div 
        v-for="(callback, index) in callbacks" 
        :key="callback.id || index" 
        class="bg-dark-secondary rounded-lg border border-gray-700 hover:border-primary/30 transition-all duration-300"
      >
        <!-- Header -->
        <div 
          class="p-4 flex items-center justify-between cursor-pointer"
          @click="toggleCallback(index)"
        >
          <div class="flex items-center space-x-4 flex-1">
            <div 
              class="transform transition-transform duration-200"
              :class="expandedCallbacks[index] ? 'rotate-90' : ''"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
            <input
              v-model="callback.title"
              type="text"
              placeholder="Enter callback name..."
              class="flex-1 px-3 py-1.5 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              @click.stop
            >
            <div class="flex items-center gap-4">
              <label class="flex items-center cursor-pointer" @click.stop>
                <div class="relative">
                  <input
                    type="checkbox"
                    v-model="callback.isActive"
                    class="sr-only"
                  >
                  <div class="w-10 h-5 bg-gray-700 rounded-full shadow-inner"></div>
                  <div
                    class="dot absolute w-4 h-4 bg-white rounded-full transition transform"
                    :class="callback.isActive ? 'translate-x-5 bg-primary' : 'translate-x-1'"
                  ></div>
                </div>
                <span class="ml-2 text-sm text-gray-400">{{ callback.isActive ? 'Active' : 'Inactive' }}</span>
              </label>
              <button
                @click.stop="removeCallback(index)"
                class="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Collapsible Content -->
        <div 
          v-show="expandedCallbacks[index]"
          class="border-t border-gray-700"
        >
          <div class="p-4 space-y-4">
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-400">JavaScript Code</label>
              <div class="relative">
                <CodeEditor
                  v-model="callback.code"
                  :schema="contextSchema"
                />
              </div>
            </div>

            <!-- Save Button -->
            <div class="flex justify-end items-center gap-3">
              <span v-if="callback.lastSaved" class="text-xs text-gray-500">
                Last saved: {{ new Date(callback.lastSaved).toLocaleString() }}
              </span>
              <button
                @click="saveCallback(index)"
                class="px-4 py-2 bg-primary text-white hover:bg-primary/90 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                :disabled="isSaving"
              >
                <span v-if="isSaving && savingIndex === index" class="inline-block animate-spin">
                  <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
                <span>{{ isSaving && savingIndex === index ? 'Saving...' : 'Save Callback' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add New Callback Button -->
      <button
        @click="addCallback"
        class="w-full px-4 py-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-primary hover:border-primary transition-colors text-sm"
      >
        Add New Callback
      </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { db } from '@/config/firebase'
import SchemaBuilder, { type SchemaField } from '@/components/content/SchemaBuilder.vue'
import CodeEditor from '@/components/content/CodeEditor.vue'
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  setDoc
} from 'firebase/firestore'

interface Callback {
  id?: string
  title: string
  isActive: boolean
  code: string
  lastSaved?: number
  created_at?: Timestamp
  updated_at?: Timestamp
}

const authStore = useAuthStore()
const isLoading = ref<boolean>(true)
const isSaving = ref<boolean>(false)
const isSavingSchema = ref<boolean>(false)
const savingIndex = ref<number | null>(null)
const error = ref<string | null>(null)
const callbacks = ref<Callback[]>([])
const contextSchema = ref<SchemaField[]>([])
const integrationSchema = ref<SchemaField[]>([])
const isSavingIntegrationSchema = ref<boolean>(false)

// Tabs
const currentTab = ref('callbacks')
const tabs = [
  { id: 'callbacks', name: 'Callbacks' },
  { id: 'schema', name: 'Personalised Data' },
  { id: 'integration', name: 'Integration Data' }
]

const expandedCallbacks = reactive<{ [key: number]: boolean }>({})

const loadCallbacks = async () => {
  if (!authStore.user) {
    error.value = 'No user logged in'
    return
  }

  isLoading.value = true
  error.value = null

  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Query callbacks for this org
    const callbacksQuery = query(
      collection(db, 'callbacks'),
      where('org_id', '==', org_id)
    )

    const querySnapshot = await getDocs(callbacksQuery)
    
    callbacks.value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title,
      code: doc.data().callback,
      isActive: doc.data().is_active,
      created_at: doc.data().created_at,
      updated_at: doc.data().updated_at,
      lastSaved: doc.data().updated_at?.toMillis()
    }))

    // If no callbacks exist, create an empty one
    if (callbacks.value.length === 0) {
      callbacks.value.push({
        title: '',
        isActive: true,
        code: ''
      })
    }

    // Initialize expanded state
    callbacks.value.forEach((_, index) => {
      expandedCallbacks[index] = index === 0
    })

  } catch (err: any) {
    console.error('Error loading callbacks:', err)
    error.value = err.message
  } finally {
    isLoading.value = false
  }
}

const toggleCallback = (index: number) => {
  expandedCallbacks[index] = !expandedCallbacks[index]
}

const addCallback = () => {
  const newIndex = callbacks.value.length
  callbacks.value.push({
    title: '',
    isActive: true,
    code: ''
  })
  expandedCallbacks[newIndex] = true
}

const removeCallback = async (index: number) => {
  const callback = callbacks.value[index]
  
  if (callback.id) {
    try {
      await deleteDoc(doc(db, 'callbacks', callback.id))
    } catch (err) {
      console.error('Error deleting callback:', err)
      return
    }
  }

  callbacks.value.splice(index, 1)
  delete expandedCallbacks[index]
  
  // Reindex remaining expanded states
  for (let i = index; i < callbacks.value.length; i++) {
    expandedCallbacks[i] = expandedCallbacks[i + 1]
    delete expandedCallbacks[i + 1]
  }

  // If no callbacks left, add an empty one
  if (callbacks.value.length === 0) {
    addCallback()
  }
}

const saveCallback = async (index: number) => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  const callback = callbacks.value[index]
  if (!callback.title.trim() || !callback.code.trim()) {
    console.error('Title and code are required')
    return
  }

  isSaving.value = true
  savingIndex.value = index

  try {
    // Get user's document to get org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Prepare callback data
    const callbackData = {
      org_id,
      title: callback.title.trim(),
      callback: callback.code.trim(),
      is_active: callback.isActive,
      updated_at: serverTimestamp()
    }

    let docRef;
    
    if (callback.id) {
      // Update existing document
      docRef = doc(db, 'callbacks', callback.id)
      await updateDoc(docRef, callbackData)
    } else {
      // Create new document
      docRef = await addDoc(collection(db, 'callbacks'), {
        ...callbackData,
        created_at: serverTimestamp()
      })
      // Store the document ID
      callbacks.value[index].id = docRef.id
    }

    // Update last saved timestamp
    callbacks.value[index].lastSaved = Date.now()
    
    console.log('Callback saved successfully')
  } catch (error) {
    console.error('Error saving callback:', error)
  } finally {
    isSaving.value = false
    savingIndex.value = null
  }
}

const saveSchema = async (schema: any[]) => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  isSavingSchema.value = true
  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Save schema to org config
    await setDoc(doc(db, 'config', org_id), {
      context_schema: schema
    }, { merge: true })

    contextSchema.value = schema
  } catch (error: any) {
    console.error('Error saving schema:', error)
  } finally {
    isSavingSchema.value = false
  }
}

const loadContextSchema = async () => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Load schema from org config
    const configDoc = await getDoc(doc(db, 'config', org_id))
    if (configDoc.exists()) {
      contextSchema.value = configDoc.data().context_schema || []
    }
  } catch (error: any) {
    console.error('Error loading schema:', error)
  }
}

const saveIntegrationSchema = async (schema: any[]) => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  isSavingIntegrationSchema.value = true
  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Save schema to org config
    await setDoc(doc(db, 'config', org_id), {
      integration_schema: schema
    }, { merge: true })

    integrationSchema.value = schema
  } catch (error: any) {
    console.error('Error saving integration schema:', error)
  } finally {
    isSavingIntegrationSchema.value = false
  }
}

const loadIntegrationSchema = async () => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Load schema from org config
    const configDoc = await getDoc(doc(db, 'config', org_id))
    if (configDoc.exists()) {
      integrationSchema.value = configDoc.data().integration_schema || []
    }
  } catch (error: any) {
    console.error('Error loading integration schema:', error)
  }
}

// Load data when component mounts
onMounted(() => {
  loadCallbacks()
  loadContextSchema()
  loadIntegrationSchema()
})
</script>

<style scoped>
.dot {
  top: 0.125rem;
}
</style>